/* Modal de envío masivo WhatsApp / Email desde Leads fríos (Pipeline). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from './ThemeProvider';
import api from '../services/api';
import { buildWhatsAppQuickReplies } from '../utils/whatsappQuickReplies';

const wa = {
  green: '#16a34a',
  greenFrom: '#15803d',
  greenTo: '#16a34a',
  greenBg: 'rgba(22,163,74,0.08)',
  greenBorder: 'rgba(22,163,74,0.20)',
  chipText: '#4ade80',
};

const WA_RETRY_WAIT_MS = 25_000;
const WA_MAX_SEND_RETRIES = 2;
const PAUSA_ENTRE_ENVIOS_MS = 450;
const ANA_WA_SESSION_ID = 'f1f7f652-819e-43c8-9ba0-2f02afbc14a6';

function primerNombre(n) {
  const t = String(n || '').trim();
  return t ? t.split(/\s+/)[0] : '';
}

function aplicarPlaceholders(texto, nombreCliente, nombreAgente) {
  let s = String(texto ?? '');
  s = s.replace(/\{nombre\}/gi, nombreCliente || '');
  s = s.replace(/\{agente\}/gi, nombreAgente || '');
  return s.replace(/VitalCasa/gi, 'Vitalcasa');
}

function nombreLead(lead) {
  return `${lead?.nombre || ''} ${lead?.apellido || ''}`.trim() || 'Cliente';
}

function telefonoVisibleLead(lead) {
  return String(
    lead?.telefono ||
    lead?.whatsapp ||
    lead?.cliente?.telefono ||
    lead?.cliente?.whatsapp ||
    ''
  ).trim();
}

function telefonoVisibleDigits(lead) {
  return telefonoVisibleLead(lead).replace(/\D/g, '');
}

function emailLead(lead) {
  return String(lead?.email || lead?.cliente?.email || '').trim();
}

function normalizarContactoLead(lead) {
  return {
    ...lead,
    telefono: lead?.telefono ?? '',
    email: emailLead(lead),
    cliente: lead?.cliente || null,
  };
}

function planificarEnvioLead(lead) {
  // WhatsApp solo si el campo teléfono del lead tiene número (como en la tabla de Fríos).
  // Si borras el teléfono pero queda whatsapp interno en BD, cae a email.
  const tel = telefonoVisibleDigits(lead);
  const email = emailLead(lead);
  const nombre = nombreLead(lead);
  if (tel.length >= 9) {
    return { lead, nombre, canal: 'wa', dest: telefonoVisibleLead(lead), subsidiario: false };
  }
  if (email) {
    return { lead, nombre, canal: 'email', dest: email, subsidiario: true };
  }
  return { lead, nombre, canal: null, dest: null, subsidiario: false };
}

async function refrescarLeadsDesdeApi(leadsIniciales = []) {
  if (!leadsIniciales.length) return [];
  const out = await Promise.all(
    leadsIniciales.map(async (l) => {
      if (!l?.id) return normalizarContactoLead(l);
      try {
        const r = await api.get(`/leads/${l.id}`);
        const d = r.data?.data;
        if (!d?.id) return normalizarContactoLead(l);
        return normalizarContactoLead(d);
      } catch {
        return normalizarContactoLead(l);
      }
    }),
  );
  return out;
}

async function fetchJsonWa(url, options) {
  const resp = await fetch(url, options);
  return resp.json();
}

async function kickWhatsappReconnect(token) {
  await fetch('/api/whatsapp/conectar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ forzar_qr: false }),
  }).catch(() => {});
}

async function enviarWaConReintento({ url, options, token }) {
  let lastData = null;
  for (let intento = 0; intento <= WA_MAX_SEND_RETRIES; intento += 1) {
    lastData = await fetchJsonWa(url, options);
    if (lastData.ok) return lastData;
    if (!lastData.canRetry || intento >= WA_MAX_SEND_RETRIES) break;
    await kickWhatsappReconnect(token);
    await new Promise((r) => setTimeout(r, WA_RETRY_WAIT_MS));
  }
  return lastData;
}

async function fetchPropiedadParaEnvio(ref) {
  const trimmed = String(ref || '').trim();
  if (!trimmed) return null;
  const r = await api.get(`/propiedades/validar-ref/${encodeURIComponent(trimmed)}`);
  if (!r.data?.existe || !r.data?.propiedad?.id) return null;
  const detRes = await api.get(`/propiedades/${r.data.propiedad.id}`);
  const p = detRes.data?.data || detRes.data?.propiedad || detRes.data || {};
  return {
    id: p.id,
    titulo: p.titulo || 'Sin título',
    detalle: `${p.tipo_nombre || p.tipo || ''} · ${p.poblacion_nombre || p.poblacion || ''}`.trim(),
    precio: p.precio ? `${Number(p.precio).toLocaleString('es-ES')} €` : 'Consultar',
    icono: '🏠',
    tipo: p.tipo_nombre || p.tipo || '',
    poblacion: p.poblacion_nombre || p.poblacion || '',
    zona: p.zona_nombre || '',
    habitaciones: p.num_dormitorios || null,
    banos: p.num_banos || null,
    m2: p.m2_construidos || null,
    referencia: p.referencia || trimmed,
    operacion: p.operacion || '',
    descripcion: p.descripcion || '',
  };
}

async function enviarWaItem({ item, mensaje, propiedad, token }) {
  const phone = String(item.dest || '').replace(/\D/g, '');
  const crmPayload = {
    lead_id: item.lead.id,
    cliente_id: item.lead.cliente_id || null,
  };
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  if (propiedad) {
    return enviarWaConReintento({
      url: '/api/whatsapp/enviar-propiedades',
      token,
      options: {
        method: 'POST',
        headers,
        body: JSON.stringify({
          telefono: phone,
          mensaje_intro: mensaje || null,
          idioma: 'ES',
          ...crmPayload,
          propiedades_ids: [propiedad.id],
          propiedades: [{
            id: propiedad.id,
            titulo: propiedad.titulo,
            tipo: propiedad.tipo,
            poblacion: propiedad.poblacion,
            zona: propiedad.zona,
            precio: propiedad.precio,
            operacion: propiedad.operacion,
            habitaciones: propiedad.habitaciones,
            banos: propiedad.banos,
            m2: propiedad.m2,
            referencia: propiedad.referencia,
            descripcion: propiedad.descripcion,
          }],
        }),
      },
    });
  }
  return enviarWaConReintento({
    url: '/api/whatsapp/enviar',
    token,
    options: {
      method: 'POST',
      headers,
      body: JSON.stringify({
        telefono: phone,
        mensaje,
        ...crmPayload,
      }),
    },
  });
}

async function enviarEmailItem({ item, mensaje, asunto, propiedad }) {
  const crmPayload = {
    lead_id: item.lead.id,
    cliente_id: item.lead.cliente_id || null,
  };
  if (propiedad) {
    const { data } = await api.post('/email/enviar-propiedades', {
      destinatario: item.dest,
      asunto,
      mensaje_intro: mensaje || null,
      ...crmPayload,
      propiedades: [{
        id: propiedad.id,
        titulo: propiedad.titulo,
        tipo: propiedad.tipo,
        poblacion: propiedad.poblacion,
        zona: propiedad.zona,
        precio: propiedad.precio,
        habitaciones: propiedad.habitaciones,
        banos: propiedad.banos,
        m2: propiedad.m2,
        referencia: propiedad.referencia,
        link: propiedad.referencia
          ? `https://www.vitalcasa.com/propiedad/${String(propiedad.referencia).trim().toLowerCase().replace(/\s+/g, '-')}`
          : '',
      }],
    });
    return data;
  }
  const { data } = await api.post('/email/enviar', {
    destinatario: item.dest,
    asunto,
    mensaje,
    ...crmPayload,
  });
  return data;
}

export default function ModalEnvioMasivoFrios({
  isOpen,
  leads = [],
  onClose,
  onComplete,
  propiedadInicial = null,
  mensajeInicial = '',
  bloquearQuitarPropiedad = false,
  modoBajadaAna = false,
  propiedadId = null,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';

  const [fase, setFase] = useState('compose');
  const [mensaje, setMensaje] = useState('');
  const [refInput, setRefInput] = useState('');
  const [refError, setRefError] = useState('');
  const [propiedad, setPropiedad] = useState(null);
  const [cargandoRef, setCargandoRef] = useState(false);
  const [agenteNombre, setAgenteNombre] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState('');
  const [progreso, setProgreso] = useState({ actual: 0, total: 0, nombre: '' });
  const [resultados, setResultados] = useState([]);
  const [sinContacto, setSinContacto] = useState([]);
  const [leadsResueltos, setLeadsResueltos] = useState([]);
  const [cargandoLeads, setCargandoLeads] = useState(false);
  const [anaTelLast9, setAnaTelLast9] = useState(null);

  const leadsInicialesRef = useRef(leads);
  leadsInicialesRef.current = leads;
  const propiedadInicialRef = useRef(propiedadInicial);
  propiedadInicialRef.current = propiedadInicial;
  const mensajeInicialRef = useRef(mensajeInicial);
  mensajeInicialRef.current = mensajeInicial;

  const plan = useMemo(() => leadsResueltos.map(planificarEnvioLead), [leadsResueltos]);
  const conEnvio = useMemo(() => plan.filter((p) => p.canal), [plan]);
  const sinEnvio = useMemo(() => plan.filter((p) => !p.canal), [plan]);
  const conflictoTelAna = useMemo(
    () =>
      anaTelLast9
        ? conEnvio.filter(
            (p) => p.canal === 'wa' && telefonoVisibleDigits(p.lead).slice(-9) === anaTelLast9,
          )
        : [],
    [conEnvio, anaTelLast9],
  );

  const modalAbiertoRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      modalAbiertoRef.current = false;
      return;
    }
    // Solo resetear al abrir el modal; no cuando el padre re-renderiza (p. ej. polling de Fríos).
    if (modalAbiertoRef.current) return;
    modalAbiertoRef.current = true;

    setFase('compose');
    setMensaje(mensajeInicialRef.current || '');
    setRefInput('');
    setRefError('');
    setPropiedad(null);
    setResultados([]);
    setSinContacto([]);
    setErrorEnvio('');
    setLeadsResueltos([]);
    setProgreso({ actual: 0, total: 0, nombre: '' });
    setCargandoLeads(true);
    const propIni = propiedadInicialRef.current;
    if (propIni?.referencia || propIni?.id) {
      const ref = String(propIni.referencia || '').trim();
      if (ref) {
        fetchPropiedadParaEnvio(ref)
          .then((p) => { if (p) setPropiedad(p); })
          .catch(() => {});
      }
    }
    refrescarLeadsDesdeApi(leadsInicialesRef.current)
      .then(setLeadsResueltos)
      .finally(() => setCargandoLeads(false));
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || '{}');
      setAgenteNombre(primerNombre(u.nombre || ''));
    } catch {
      setAgenteNombre('');
    }
    api.get('/auth/me').then((r) => {
      if (r.data?.nombre) setAgenteNombre(primerNombre(r.data.nombre));
    }).catch(() => {});
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/whatsapp/estado', { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' }).catch(() => {});
    }
    if (modoBajadaAna && token) {
      fetch(`/api/whatsapp/estado-sesion/${ANA_WA_SESSION_ID}`, {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      })
        .then((r) => r.json())
        .then((d) => {
          const phone = String(d?.data?.phone || '').replace(/\D/g, '');
          setAnaTelLast9(phone.length >= 9 ? phone.slice(-9) : null);
        })
        .catch(() => setAnaTelLast9(null));
    } else {
      setAnaTelLast9(null);
    }
  }, [isOpen, modoBajadaAna]);

  if (!isOpen) return null;

  const quickReplies = buildWhatsAppQuickReplies(propiedad ? 1 : 0, 'ES');
  const asuntoEmail = propiedad?.referencia
    ? `Propiedades seleccionadas para ti - Ref. ${propiedad.referencia}`
    : 'Propiedades seleccionadas para ti - VitalCasa';

  const añadirRef = async () => {
    const ref = refInput.trim();
    if (!ref) return;
    setCargandoRef(true);
    setRefError('');
    try {
      const p = await fetchPropiedadParaEnvio(ref);
      if (!p) {
        setRefError('Referencia no encontrada');
        return;
      }
      setPropiedad(p);
      setRefInput('');
    } catch {
      setRefError('Error al validar la referencia');
    } finally {
      setCargandoRef(false);
    }
  };

  const enviarMasivo = async () => {
    if (!mensaje.trim() && !propiedad) return;
    setErrorEnvio('');
    setEnviando(true);
    setFase('sending');
    const token = localStorage.getItem('token');
    const results = [];

    const effectivePropiedadId = propiedadId || propiedad?.id || null;

    if (modoBajadaAna) {
      if (!effectivePropiedadId) {
        setErrorEnvio('No se pudo identificar la propiedad. Cierra el modal, guarda de nuevo la ficha y reintenta.');
        setEnviando(false);
        setFase('compose');
        return;
      }
      if (conflictoTelAna.length > 0) {
        setErrorEnvio(
          'No puedes enviar la bajada al mismo móvil de Ana. Elige un lead con el teléfono del cliente (no el de Ana).',
        );
        setEnviando(false);
        setFase('compose');
        return;
      }
      const destinatarios = conEnvio.map((item) => ({
        lead_id: item.lead.id,
        cliente_id: item.lead.cliente_id || null,
        telefono: item.canal === 'wa' ? item.dest : null,
        email: item.canal === 'email' ? item.dest : null,
        canal: item.canal === 'wa' ? 'whatsapp' : 'email',
        idioma: item.lead.idioma || 'ES',
      }));
      setProgreso({ actual: 0, total: destinatarios.length, nombre: 'Enviando desde Ana…' });
      try {
        const { data } = await api.post(`/propiedades/${effectivePropiedadId}/enviar-bajada-precio`, {
          mensaje,
          destinatarios,
        });
        const batch = data?.resultados || [];
        batch.forEach((r, i) => {
          const item = conEnvio[i];
          if (!item) return;
          results.push({
            id: item.lead.id,
            nombre: item.nombre,
            canal: item.canal,
            dest: item.dest,
            subsidiario: item.subsidiario,
            ok: !!r.ok,
            error: r.error || null,
          });
        });
        setResultados([...results]);
      } catch (e) {
        const msg = e.response?.data?.error || e.message || 'Error de envío';
        setErrorEnvio(msg);
        conEnvio.forEach((item) => {
          results.push({
            id: item.lead.id,
            nombre: item.nombre,
            canal: item.canal,
            dest: item.dest,
            subsidiario: item.subsidiario,
            ok: false,
            error: msg,
          });
        });
        setResultados([...results]);
      }
      setSinContacto(sinEnvio.map((s) => ({ id: s.lead.id, nombre: s.nombre })));
      setEnviando(false);
      if (results.some((r) => r.ok)) setFase('resumen');
      else setFase('compose');
      return;
    }

    const total = conEnvio.length;

    for (let i = 0; i < conEnvio.length; i += 1) {
      const item = conEnvio[i];
      const nombreCorto = primerNombre(item.nombre) || 'cliente';
      const msgPersonal = aplicarPlaceholders(mensaje, nombreCorto, agenteNombre).slice(0, 1000);
      setProgreso({ actual: i + 1, total, nombre: item.nombre });

      let ok = false;
      let error = null;
      try {
        if (item.canal === 'wa') {
          const data = await enviarWaItem({ item, mensaje: msgPersonal, propiedad, token });
          ok = !!data?.ok;
          if (!ok) error = data?.error || data?.message || 'Error WhatsApp';
        } else {
          const data = await enviarEmailItem({ item, mensaje: msgPersonal, asunto: asuntoEmail, propiedad });
          ok = data?.ok !== false;
          if (!ok) error = data?.error || 'Error email';
        }
      } catch (e) {
        error = e.response?.data?.error || e.message || 'Error de envío';
      }

      results.push({
        id: item.lead.id,
        nombre: item.nombre,
        canal: item.canal,
        dest: item.dest,
        subsidiario: item.subsidiario,
        ok,
        error,
      });
      setResultados([...results]);
      if (i < conEnvio.length - 1) {
        await new Promise((r) => setTimeout(r, PAUSA_ENTRE_ENVIOS_MS));
      }
    }

    setSinContacto(sinEnvio.map((s) => ({ id: s.lead.id, nombre: s.nombre })));
    setEnviando(false);
    setFase('resumen');
  };

  const nOk = resultados.filter((r) => r.ok).length;
  const nWa = resultados.filter((r) => r.ok && r.canal === 'wa').length;
  const nEmail = resultados.filter((r) => r.ok && r.canal === 'email').length;
  const nFallo = resultados.filter((r) => !r.ok).length;

  const cerrarFinal = () => {
    onComplete?.({ resultados, sinContacto: sinEnvio.map((s) => ({ id: s.lead.id, nombre: s.nombre })) });
    onClose?.();
  };

  return createPortal(
    <div
      onClick={fase === 'compose' && !enviando ? onClose : undefined}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10050,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520, maxWidth: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
          background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
          border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{ height: 3, flexShrink: 0, background: `linear-gradient(90deg, ${wa.green}, ${wa.greenFrom})` }} />

        {/* Cabecera */}
        <div style={{
          padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
          background: isDark ? 'transparent' : '#e0e0d8',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>
              {modoBajadaAna ? 'Enviar bajada de precio (Ana)' : 'Enviar WhatsApp / Email'}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: c.textMuted }}>
              {modoBajadaAna
                ? 'Los WhatsApp se envían desde el móvil de Ana y los emails desde info@vitalcasa.com. Si el cliente responde con interés, se crea un lead y la tarea de agendar cita.'
                : `${leadsResueltos.length || leads.length} seleccionado${(leadsResueltos.length || leads.length) !== 1 ? 's' : ''}`}
              {cargandoLeads ? ' · actualizando contactos…' : (
                <>
                  {' · '}{conEnvio.length} recibirán mensaje
                  {sinEnvio.length > 0 && ` · ${sinEnvio.length} sin teléfono ni email`}
                </>
              )}
            </p>
          </div>
          {fase === 'compose' && !enviando && (
            <button type="button" onClick={onClose} style={{
              width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
              background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
            }}>✕</button>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px 14px' }}>
          {fase === 'compose' && cargandoLeads && (
            <div style={{ textAlign: 'center', padding: '28px 12px', fontSize: 13, color: c.textMuted }}>
              Cargando teléfonos y emails actualizados…
            </div>
          )}

          {fase === 'compose' && !cargandoLeads && (
            <>
              {errorEnvio && (
                <div style={{
                  marginBottom: 10, padding: '10px 12px', borderRadius: 10,
                  background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 12, lineHeight: 1.45,
                }}>
                  {errorEnvio}
                </div>
              )}
              <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 10, lineHeight: 1.45 }}>
                {modoBajadaAna
                  ? 'Los WhatsApp salen del móvil de Ana (no del tuyo). Si Ana no está conectada, el envío fallará.'
                  : 'Se enviará por WhatsApp si el lead tiene teléfono; si no, por email (incluido el del cliente vinculado). Placeholders: {nombre}, {agente}.'}
              </div>
              {modoBajadaAna && conflictoTelAna.length > 0 && (
                <div style={{
                  marginBottom: 10, padding: '10px 12px', borderRadius: 10,
                  background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: 12, lineHeight: 1.45,
                }}>
                  {conflictoTelAna.length === 1
                    ? 'El lead seleccionado tiene el mismo teléfono que Ana. Las respuestas en ese chat no se procesan como cliente.'
                    : `${conflictoTelAna.length} leads tienen el teléfono de Ana. Quita esos contactos o usa leads con el móvil del cliente.`}
                </div>
              )}

              {/* Referencia */}
              <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 10, background: isDark ? c.bgInput : '#fff', border: `1px solid ${c.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: wa.green, marginBottom: 6 }}>
                  {bloquearQuitarPropiedad ? 'Propiedad' : 'Referencia (opcional)'}
                </div>
                {propiedad ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 8, background: wa.greenBg, border: `1px solid ${wa.greenBorder}` }}>
                    <span style={{ fontSize: 18 }}>🏠</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary }}>{propiedad.titulo}</div>
                      <div style={{ fontSize: 11, color: c.textMuted }}>Ref. {propiedad.referencia} · {propiedad.precio}</div>
                    </div>
                    {!bloquearQuitarPropiedad && (
                      <button type="button" onClick={() => setPropiedad(null)} style={{
                        border: 'none', background: 'transparent', cursor: 'pointer', color: c.textMuted, fontSize: 14,
                      }}>✕</button>
                    )}
                  </div>
                ) : bloquearQuitarPropiedad ? (
                  <div style={{ fontSize: 12, color: c.textMuted, padding: '6px 4px' }}>Cargando ficha de la propiedad…</div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={refInput}
                      onChange={(e) => { setRefInput(e.target.value); setRefError(''); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); añadirRef(); } }}
                      placeholder="Ej. 3526-C"
                      style={{
                        flex: 1, padding: '8px 10px', borderRadius: 8, border: `1px solid ${c.border}`,
                        background: c.bgInput, color: c.textPrimary, fontSize: 13, fontFamily: 'inherit',
                      }}
                    />
                    <button type="button" onClick={añadirRef} disabled={cargandoRef || !refInput.trim()} style={{
                      padding: '8px 12px', borderRadius: 8, border: 'none', background: wa.green, color: '#fff',
                      fontWeight: 600, fontSize: 12, cursor: cargandoRef ? 'wait' : 'pointer', opacity: !refInput.trim() ? 0.5 : 1,
                    }}>
                      {cargandoRef ? '…' : 'Añadir'}
                    </button>
                  </div>
                )}
                {refError && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{refError}</div>}
              </div>

              {/* Mensaje */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: wa.green, marginBottom: 6 }}>
                  Mensaje
                </div>
                <textarea
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value.slice(0, 1000))}
                  placeholder={propiedad ? 'Texto de introducción (opcional)…' : 'Escribe tu mensaje…'}
                  rows={5}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 8,
                    border: `1.5px solid ${c.border}`, background: isDark ? c.bgInput : 'rgba(22,163,74,0.12)',
                    color: c.textPrimary, fontSize: 13.5, fontFamily: 'inherit', resize: 'vertical',
                  }}
                />
                <div style={{ textAlign: 'right', fontSize: 10, color: c.textMuted, marginTop: 4 }}>{mensaje.length} / 1.000</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {quickReplies.map((qr) => (
                    <button
                      key={qr.id}
                      type="button"
                      onClick={() => setMensaje(aplicarPlaceholders(qr.text, 'cliente', agenteNombre).slice(0, 1000))}
                      style={{
                        padding: '4px 8px', borderRadius: 5, border: `1px solid ${wa.greenBorder}`,
                        background: wa.greenBg, color: wa.chipText, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      {qr.emoji} {qr.label}
                    </button>
                  ))}
                </div>
              </div>

              {sinEnvio.length > 0 && (
                <div style={{
                  padding: '8px 10px', borderRadius: 8, fontSize: 11, lineHeight: 1.4,
                  background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb', color: isDark ? '#fcd34d' : '#92400e',
                  border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : '#fde68a'}`,
                }}>
                  {sinEnvio.length} lead{sinEnvio.length !== 1 ? 's' : ''} no tienen teléfono ni email: se listarán al final sin enviar.
                </div>
              )}
            </>
          )}

          {fase === 'sending' && (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>Enviando…</div>
              <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 16 }}>
                {progreso.actual} / {progreso.total} — {progreso.nombre}
              </div>
              <div style={{ height: 6, borderRadius: 3, background: c.border, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3, background: wa.green,
                  width: progreso.total ? `${(progreso.actual / progreso.total) * 100}%` : '0%',
                  transition: 'width 0.25s',
                }} />
              </div>
            </div>
          )}

          {fase === 'resumen' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: 8, marginBottom: 14 }}>
                {nWa > 0 && (
                  <div style={{ padding: '8px 10px', borderRadius: 8, textAlign: 'center', background: wa.greenBg, border: `1px solid ${wa.greenBorder}` }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: wa.green }}>{nWa}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: wa.green }}>WhatsApp</div>
                  </div>
                )}
                {nEmail > 0 && (
                  <div style={{ padding: '8px 10px', borderRadius: 8, textAlign: 'center', background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.25)' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#0ea5e9' }}>{nEmail}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#0369a1' }}>Email</div>
                  </div>
                )}
                {nFallo > 0 && (
                  <div style={{ padding: '8px 10px', borderRadius: 8, textAlign: 'center', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: '#dc2626' }}>{nFallo}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#dc2626' }}>Fallidos</div>
                  </div>
                )}
              </div>

              {nFallo > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 6 }}>Envíos fallidos</div>
                  {resultados.filter((r) => !r.ok).map((r) => (
                    <div key={r.id} style={{ fontSize: 12, padding: '6px 10px', marginBottom: 4, borderRadius: 6, background: 'rgba(239,68,68,0.06)', color: c.textSecondary }}>
                      <strong>{r.nombre}</strong> — {r.error || 'Error'}
                    </div>
                  ))}
                </div>
              )}

              {sinContacto.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: c.textPrimary, marginBottom: 8 }}>
                    Sin teléfono ni email ({sinContacto.length})
                  </div>
                  <p style={{ fontSize: 11, color: c.textMuted, margin: '0 0 8px', lineHeight: 1.4 }}>
                    Estos leads estaban seleccionados pero no tienen WhatsApp ni correo para recibir el mensaje.
                  </p>
                  <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${c.border}`, borderRadius: 8 }}>
                    {sinContacto.map((s) => (
                      <div key={s.id} style={{
                        padding: '8px 12px', fontSize: 13, borderBottom: `1px solid ${c.border}`,
                        color: c.textSecondary, display: 'flex', alignItems: 'center', gap: 8,
                      }}>
                        <span style={{ color: '#94a3b8' }}>—</span>
                        <span style={{ fontWeight: 600, color: c.textPrimary }}>{s.nombre}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {nOk === resultados.length && sinContacto.length === 0 && (
                <div style={{ textAlign: 'center', padding: '12px 0', fontSize: 14, fontWeight: 600, color: wa.green }}>
                  Todos los envíos completados correctamente.
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '10px 14px 14px', borderTop: `1px solid ${c.border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {fase === 'compose' && (
            <>
              <button type="button" onClick={onClose} style={{
                padding: '9px 16px', borderRadius: 8, border: `1px solid ${c.border}`,
                background: 'transparent', color: c.textSecondary, fontWeight: 600, cursor: 'pointer',
              }}>
                Cancelar
              </button>
              <button
                type="button"
                onClick={enviarMasivo}
                disabled={
                  enviando
                  || cargandoLeads
                  || (!mensaje.trim() && !propiedad)
                  || conEnvio.length === 0
                  || (modoBajadaAna && conflictoTelAna.length > 0)
                }
                style={{
                  padding: '9px 18px', borderRadius: 8, border: 'none', background: wa.green, color: '#fff',
                  fontWeight: 700,
                  cursor: conEnvio.length === 0 || (modoBajadaAna && conflictoTelAna.length > 0) ? 'not-allowed' : 'pointer',
                  opacity: (!mensaje.trim() && !propiedad) || conEnvio.length === 0 || (modoBajadaAna && conflictoTelAna.length > 0) ? 0.5 : 1,
                }}
              >
                Enviar a {conEnvio.length} contacto{conEnvio.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
          {fase === 'resumen' && (
            <button type="button" onClick={cerrarFinal} style={{
              padding: '9px 18px', borderRadius: 8, border: 'none', background: wa.green, color: '#fff',
              fontWeight: 700, cursor: 'pointer', width: '100%',
            }}>
              Cerrar
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
